/**
 * TQ #6166 Bundle-D — Aria-Org-Settings (Org-Owner-Sicht)
 * Phase 1 Cockpit-Sprint per PROJ-TQ6092-Aria-Org-Settings-Phase1.md
 *
 * 4 Tabs:
 * - Persona (Tonalitaet, Soul-Override)
 * - Org-Defaults (Tool-Aktivierung, Geteilte Postfaecher)
 * - GroupFolders (Nextcloud Sichtbarkeit pro KC-Group)
 * - Audit-Log (Filter Datum/Actor/Action-Type)
 *
 * Distinct von components/AriaSettings/AriaSettings.tsx (User-per-Sidecar-Cred-Sicht).
 *
 * API-Endpoints (queue: Bundle-C #6165):
 * - GET/PUT /api/org/{slug}/aria-settings
 * - GET/PATCH /api/org/{slug}/shared-mailboxes
 * - GET/PATCH/POST /api/org/{slug}/groupfolder-map
 * - GET /api/org/{slug}/audit-log
 *
 * Mock-Data faellt zurueck bei 404 — entfernen sobald portal-api die Routes liefert.
 */
import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import PrivacyModeSelector from '../components/PrivacyModeSelector'
import { apiOrigin, isTenantInstance } from '../lib/runtimeConfig'
import NotAvailableNotice from '../components/NotAvailableNotice'
import './AriaOrgSettings.css'

interface Organization {
  slug: string
  name: string
  member_role?: string
}

interface AriaOrgSettingsProps {
  organization: Organization
  /** TQ #6298: Wenn von CortexPage als embedded Tab gerendert: aktiver Tab
      kommt von aussen (URL ?tab=...), interne Tab-Bar wird ausgeblendet
      (Tab-Switch passiert oben in CortexPage). */
  activeTab?: Tab
  hideTabBar?: boolean
  /** TQ #491 Phase 2: mehrere Abschnitte in EINER Instanz (ein State, ein Speichern-Knopf).
      Zwei Instanzen mit je eigenem State wuerden sich beim Speichern gegenseitig
      ueberschreiben (PUT schickt immer alle Felder, Last-Writer-Wins). Hat Vorrang vor activeTab. */
  sections?: ReadonlyArray<Tab>
}

export type Tab = 'persona' | 'defaults' | 'groupfolders' | 'audit' | 'datenschutz'
type Tonality = 'professional' | 'casual' | 'formal'

// ─── Data types (match plan PROJ-TQ6092-Aria-Org-Settings-Phase1.md Section 3) ───

interface AvailableTool {
  id: string
  label: string
  default_enabled: boolean
}

interface AriaSettings {
  tenant_slug: string
  aria_tonality: Tonality
  aria_soul_override: string | null
  deferred_tools_enabled: string[]
  nc_groupfolder_sync_enabled: boolean
  nc_groupfolder_sync_last_run: string | null
  available_tools: AvailableTool[]
  updated_at: string | null
  updated_by: string | null
}

interface SharedMailbox {
  address: string
  label: string | null
  aria_can_read: boolean
  aria_can_send: boolean
}

interface GroupFolder {
  id: number
  name: string
  visible: boolean
}

interface GroupFolderGroup {
  kc_group_path: string
  members_count: number
  groupfolders: GroupFolder[]
}

interface AuditEvent {
  id: string
  event_type: string
  occurred_at: string
  actor_sub: string | null
  actor_email: string | null
  actor_display_name: string | null
  diff: Record<string, { from: unknown; to: unknown }> | null
}

// ─── Tool-Liste ───────────────────────────────────────────────────────────────
// TQ #6617: Die verfügbaren Tools kommen AUSSCHLIESSLICH aus der API
// (cortex available_tools_cache, tenant-scoped, via portal-api Proxy). Es gibt
// KEINE hartkodierte Fallback-Liste mehr — eine leere/ladende Liste rendert
// einen expliziten Leerzustand statt erfundener Tools (vorher fälschlich
// nextcloud/lexoffice, obwohl IT-SCE OpenCloud nutzt und kein Lexoffice).
const NO_TOOLS: AvailableTool[] = []

// Cortex /api/v1/admin/available-tools antwortet als
// { sidecars: [{ sidecar_name, tools: [{ tool_id, label, default_enabled, … }] }], total_tools }.
// portal-api reicht dieses Objekt unter `available_tools` durch. Hier flach auf
// AvailableTool[] mappen. Akzeptiert auch ein bereits flaches Array (Robustheit).
function flattenAvailableTools(raw: unknown): AvailableTool[] {
  const toTool = (t: Record<string, unknown>): AvailableTool | null => {
    const id = (t.tool_id ?? t.id) as string | undefined
    if (!id) return null
    return {
      id,
      label: (t.label as string) ?? id,
      default_enabled: Boolean(t.default_enabled),
    }
  }
  if (Array.isArray(raw)) {
    return raw
      .map((t) => toTool(t as Record<string, unknown>))
      .filter((t): t is AvailableTool => t !== null)
  }
  if (raw && typeof raw === 'object') {
    const sidecars = (raw as Record<string, unknown>).sidecars
    if (Array.isArray(sidecars)) {
      const out: AvailableTool[] = []
      for (const grp of sidecars) {
        const tools = (grp as Record<string, unknown>)?.tools
        if (Array.isArray(tools)) {
          for (const t of tools) {
            const mapped = toTool(t as Record<string, unknown>)
            if (mapped) out.push(mapped)
          }
        }
      }
      return out
    }
  }
  return NO_TOOLS
}

// Backend antwortet mit { settings: {...}, never_configured: bool } und liefert
// weder available_tools noch tenant_slug/updated_*. Normalisieren auf das
// Frontend-Shape, mit Defaults fuer alle Pflicht-Felder.
function normalizeSettings(
  raw: unknown,
  slug: string,
): AriaSettings {
  const obj = (raw ?? {}) as Record<string, unknown>
  const inner = (obj.settings && typeof obj.settings === 'object'
    ? (obj.settings as Record<string, unknown>)
    : obj)
  return {
    tenant_slug: (inner.tenant_slug as string) ?? slug,
    aria_tonality: ((inner.aria_tonality as Tonality) ?? 'professional'),
    aria_soul_override: (inner.aria_soul_override as string | null) ?? null,
    deferred_tools_enabled: Array.isArray(inner.deferred_tools_enabled)
      ? (inner.deferred_tools_enabled as string[])
      : [],
    nc_groupfolder_sync_enabled: Boolean(inner.nc_groupfolder_sync_enabled),
    nc_groupfolder_sync_last_run:
      (inner.nc_groupfolder_sync_last_run as string | null) ?? null,
    available_tools: flattenAvailableTools(inner.available_tools),
    updated_at: (inner.updated_at as string | null) ?? null,
    updated_by: (inner.updated_by as string | null) ?? null,
  }
}

// ─── Mock-Data (Fallback bis Bundle-C portal-api live) ─────────────────────────

const MOCK_SETTINGS = (slug: string): AriaSettings => ({
  tenant_slug: slug,
  aria_tonality: 'professional',
  aria_soul_override: null,
  deferred_tools_enabled: [],
  nc_groupfolder_sync_enabled: false,
  nc_groupfolder_sync_last_run: null,
  available_tools: NO_TOOLS,
  updated_at: null,
  updated_by: null,
})

const MOCK_MAILBOXES: SharedMailbox[] = [
  { address: 'info@example.de', label: 'Allgemeine Anfragen', aria_can_read: false, aria_can_send: false },
  { address: 'buchhaltung@example.de', label: 'Buchhaltung', aria_can_read: false, aria_can_send: false },
]

const MOCK_GROUPFOLDERS: GroupFolderGroup[] = [
  {
    kc_group_path: '/teams/buchhaltung',
    members_count: 4,
    groupfolders: [
      { id: 12, name: 'Buchhaltung', visible: true },
      { id: 7, name: 'Vertraege', visible: false },
    ],
  },
  {
    kc_group_path: '/teams/baustelle',
    members_count: 8,
    groupfolders: [{ id: 15, name: 'Projektplaene', visible: true }],
  },
]

const MOCK_AUDIT: AuditEvent[] = [
  {
    id: '1',
    event_type: 'aria.settings.changed',
    occurred_at: new Date().toISOString(),
    actor_sub: 'kc-mock-123',
    actor_email: 'admin@example.de',
    actor_display_name: 'Admin Mock',
    diff: { aria_tonality: { from: 'professional', to: 'casual' } },
  },
]

// ─── API-Wrapper (mit Fallback auf Mock-Data) ──────────────────────────────────


/** Kunden-Instanz: Route gibt es dort nicht (TQ #409). Fehler statt Daten, fail-closed. */
export class TenantUnavailableError extends Error {
  constructor(path: string) {
    super(`In der Kunden-Instanz nicht verfuegbar: ${path}`)
    this.name = 'TenantUnavailableError'
  }
}

export async function fetchJson<T>(
  path: string,
  init: RequestInit,
  token: string,
  mockFallback: T,
): Promise<{ data: T; mocked: boolean }> {
  // Kunden-Instanz: cockpit-api hat diese Routen nicht — kein Request und NIE Mock-Daten
  // (QM-bf Review 981): Fehler statt Fallback, die Aufrufer laden im Instanz-Modus gar nicht erst.
  if (isTenantInstance()) throw new TenantUnavailableError(path)
  try {
    const resp = await fetch(`${apiOrigin()}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init.headers || {}),
      },
    })
    if (resp.status === 404) {
      console.warn(`[AriaOrgSettings] ${path} returned 404 — using mock data`)
      return { data: mockFallback, mocked: true }
    }
    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`)
    }
    return { data: (await resp.json()) as T, mocked: false }
  } catch (err) {
    console.warn(`[AriaOrgSettings] ${path} failed — using mock data:`, err)
    return { data: mockFallback, mocked: true }
  }
}

// ─── Sub-Komponenten ───────────────────────────────────────────────────────────

function PersonaTab({
  settings,
  onChange,
}: {
  settings: AriaSettings
  onChange: (patch: Partial<AriaSettings>) => void
}) {
  return (
    <div id="persona" className="aria-org-tab">
      <section className="aria-org-section">
        <h3>Tonalität</h3>
        <p className="aria-org-section-hint">
          Wie soll Aria mit dir und deinem Team sprechen?
        </p>
        <div className="aria-org-tonality-radio">
          {(['professional', 'casual', 'formal'] as Tonality[]).map((t) => (
            <label key={t} className={`aria-org-radio ${settings.aria_tonality === t ? 'active' : ''}`}>
              <input
                type="radio"
                name="tonality"
                value={t}
                checked={settings.aria_tonality === t}
                onChange={() => onChange({ aria_tonality: t })}
              />
              <span>
                {t === 'professional' && 'Professionell'}
                {t === 'casual' && 'Locker'}
                {t === 'formal' && 'Förmlich'}
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="aria-org-section">
        <h3>Persona-Erweiterung (Soul-Addendum)</h3>
        <p className="aria-org-section-hint">
          Zusätzliche Anweisungen für Arias Persönlichkeit. Überschreibt die Standard-Persona.
        </p>
        <textarea
          className="aria-org-textarea"
          rows={6}
          maxLength={2000}
          placeholder="z.B. Wir sind ein Bauunternehmen, sprich kurz und direkt."
          value={settings.aria_soul_override ?? ''}
          onChange={(e) => onChange({ aria_soul_override: e.target.value || null })}
        />
        <div className="aria-org-textarea-footer">
          <span>{(settings.aria_soul_override ?? '').length}/2000 Zeichen</span>
          {settings.aria_soul_override && (
            <button
              className="aria-org-btn-secondary"
              onClick={() => onChange({ aria_soul_override: null })}
            >
              Persona-Erweiterung leeren
            </button>
          )}
        </div>
      </section>
    </div>
  )
}

function DefaultsTab({
  settings,
  mailboxes,
  onSettingsChange,
  onMailboxToggle,
  onMailboxSync,
  mailboxSyncing,
}: {
  settings: AriaSettings
  mailboxes: SharedMailbox[]
  onSettingsChange: (patch: Partial<AriaSettings>) => void
  onMailboxToggle: (address: string, canRead: boolean) => void
  onMailboxSync: () => void
  mailboxSyncing: boolean
}) {
  const enabledTools = settings.deferred_tools_enabled ?? []
  const tools = settings.available_tools ?? []
  const toggleTool = (id: string) => {
    const set = new Set(enabledTools)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    onSettingsChange({ deferred_tools_enabled: Array.from(set) })
  }
  return (
    <div id="org-defaults" className="aria-org-tab">
      <section className="aria-org-section">
        <h3>Tool-Aktivierung</h3>
        <p className="aria-org-section-hint">
          Welche Tools darf Aria nutzen (aktiviert in der gesamten Org).
        </p>
        <div className="aria-org-tool-list">
          {tools.length === 0 ? (
            <p className="aria-org-table-empty">
              Keine Tools verfügbar. Entweder hat diese Organisation noch keine
              Aria-Module gebucht oder die Tool-Liste wird gerade geladen.
            </p>
          ) : (
            tools.map((t) => (
              <label key={t.id} className="aria-org-checkbox">
                <input
                  type="checkbox"
                  checked={enabledTools.includes(t.id)}
                  onChange={() => toggleTool(t.id)}
                />
                <span className="aria-org-tool-id">{t.id}</span>
                <span className="aria-org-tool-label">{t.label}</span>
              </label>
            ))
          )}
        </div>
      </section>

      <section className="aria-org-section">
        <h3>Geteilte Postfächer</h3>
        <p className="aria-org-section-hint">
          Welche Org-Postfächer darf Aria lesen (z.B. info@, buchhaltung@).
        </p>
        <div className="aria-org-sync-row">
          <button
            className="aria-org-btn-secondary"
            onClick={onMailboxSync}
            disabled={mailboxSyncing}
          >
            {mailboxSyncing ? 'Synchronisiere…' : 'Postfächer von Stalwart synchronisieren'}
          </button>
        </div>
        <table className="aria-org-table">
          <thead>
            <tr>
              <th>Adresse</th>
              <th>Label</th>
              <th>Aria liest</th>
            </tr>
          </thead>
          <tbody>
            {mailboxes.length === 0 && (
              <tr>
                <td colSpan={3} className="aria-org-table-empty">
                  Keine Postfächer entdeckt. Sync starten.
                </td>
              </tr>
            )}
            {mailboxes.map((m) => (
              <tr key={m.address}>
                <td className="aria-org-mono">{m.address}</td>
                <td>{m.label ?? '—'}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={m.aria_can_read}
                    onChange={(e) => onMailboxToggle(m.address, e.target.checked)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}

function GroupFoldersTab({
  groups,
  syncEnabled,
  lastSyncRun,
  onToggleVisible,
  onSync,
  syncing,
  onSyncEnabledChange,
}: {
  groups: GroupFolderGroup[]
  syncEnabled: boolean
  lastSyncRun: string | null
  onToggleVisible: (kcGroup: string, gfId: number, visible: boolean) => void
  onSync: () => void
  syncing: boolean
  onSyncEnabledChange: (enabled: boolean) => void
}) {
  return (
    <div id="groupfolders" className="aria-org-tab">
      <section className="aria-org-section">
        <h3>Nextcloud GroupFolder-Sichtbarkeit</h3>
        <p className="aria-org-section-hint">
          Welche Nextcloud-GroupFolders darf Aria sehen (read-only). Sync läuft per
          KC-Group-Membership (Owner-Gruppen werden via OIDC-Hard-Mode-Sync nach NC propagiert).
        </p>
        <div className="aria-org-sync-row">
          <label className="aria-org-toggle">
            <input
              type="checkbox"
              checked={syncEnabled}
              onChange={(e) => onSyncEnabledChange(e.target.checked)}
            />
            <span>Auto-Sync aktivieren (nightly + KC-Webhook)</span>
          </label>
          <button className="aria-org-btn-secondary" onClick={onSync} disabled={syncing || !syncEnabled}>
            {syncing ? 'Synchronisiere…' : 'Jetzt synchronisieren'}
          </button>
          {lastSyncRun && (
            <span className="aria-org-sync-info">
              Letzter Sync: {new Date(lastSyncRun).toLocaleString('de-DE')}
            </span>
          )}
        </div>
        {groups.length === 0 ? (
          <p className="aria-org-table-empty">
            Keine GroupFolders synchronisiert. Sync starten.
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.kc_group_path} className="aria-org-gf-group">
              <div className="aria-org-gf-group-header">
                <span className="aria-org-mono">{g.kc_group_path}</span>
                <span className="aria-org-gf-members">{g.members_count} Mitglieder</span>
              </div>
              <table className="aria-org-table">
                <thead>
                  <tr>
                    <th>GroupFolder</th>
                    <th>Aria sieht</th>
                  </tr>
                </thead>
                <tbody>
                  {g.groupfolders.map((gf) => (
                    <tr key={gf.id}>
                      <td>{gf.name}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={gf.visible}
                          onChange={(e) =>
                            onToggleVisible(g.kc_group_path, gf.id, e.target.checked)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </section>
    </div>
  )
}

// TQ #6128: Audit-Log Tab mit Server-seitigen Filtern (Datum von/bis, Actor,
// Action-Type) + Cursor-basiertem "Mehr laden". Filter werden NICHT mehr
// client-seitig angewandt — der "Anwenden"-Button triggert onApply (Re-Fetch
// mit Query-Params an den #6127-Reader).
function AuditTab({
  events,
  filterType,
  setFilterType,
  since,
  setSince,
  until,
  setUntil,
  actor,
  setActor,
  onApply,
  onLoadMore,
  hasMore,
  loading,
}: {
  events: AuditEvent[]
  filterType: string
  setFilterType: (t: string) => void
  since: string
  setSince: (s: string) => void
  until: string
  setUntil: (s: string) => void
  actor: string
  setActor: (s: string) => void
  onApply: () => void
  onLoadMore: () => void
  hasMore: boolean
  loading: boolean
}) {
  return (
    <div className="aria-org-tab">
      <section className="aria-org-section">
        <h3>Audit-Log</h3>
        <p className="aria-org-section-hint">
          Wer hat wann Aria-Org-Settings geändert?
        </p>
        <div className="aria-org-filter-row">
          <label>
            Aktion:&nbsp;
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">Alle</option>
              <option value="aria.settings">Settings-Änderungen</option>
              <option value="aria.groupfolder">GroupFolder-Sync</option>
              <option value="aria.shared_mailbox">Mailbox-Änderungen</option>
            </select>
          </label>
          <label>
            Von:&nbsp;
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
          </label>
          <label>
            Bis:&nbsp;
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          </label>
          <label>
            Wer:&nbsp;
            <input
              type="text"
              value={actor}
              placeholder="user@…"
              onChange={(e) => setActor(e.target.value)}
            />
          </label>
          <button type="button" onClick={onApply} disabled={loading}>
            {loading ? 'Lädt…' : 'Anwenden'}
          </button>
        </div>
        <table className="aria-org-table">
          <thead>
            <tr>
              <th>Zeit</th>
              <th>Wer</th>
              <th>Aktion</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr>
                <td colSpan={4} className="aria-org-table-empty">
                  Keine Einträge.
                </td>
              </tr>
            )}
            {events.map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.occurred_at).toLocaleString('de-DE')}</td>
                <td>{e.actor_display_name ?? e.actor_email ?? e.actor_sub ?? '—'}</td>
                <td className="aria-org-mono">{e.event_type}</td>
                <td>
                  {e.diff && (
                    <details>
                      <summary>Diff anzeigen</summary>
                      <pre className="aria-org-diff">{JSON.stringify(e.diff, null, 2)}</pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasMore && (
          <div className="aria-org-loadmore">
            <button type="button" onClick={onLoadMore} disabled={loading}>
              {loading ? 'Lädt…' : 'Mehr laden'}
            </button>
          </div>
        )}
      </section>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

function AriaOrgSettingsInner({
  organization,
  activeTab,
  hideTabBar = false,
  sections,
}: AriaOrgSettingsProps) {
  const { token } = useAuth()
  const slug = organization.slug

  // TQ #8459/#10086: Datenschutz-Tab ist fuer ALLE Org-Mitglieder lesbar (nicht
  // nur Admin/Owner) -- Aendern bleibt server-seitig admin/owner-only (siehe
  // PrivacyModeSelector canEdit-Prop unten + api/privacy_mode.rs Backend-Gate).
  const role = organization.member_role
  const isAdminOrOwner = !role || role === 'admin' || role === 'owner'

  // TQ #6298: Controlled mode wenn activeTab gesetzt (von CortexPage Tab-Host).
  // Sonst interner State wie bisher (Legacy /aria/settings Route — sollte
  // nicht mehr direkt aufgerufen werden, aber Backwards-Compat).
  const [internalTab, setInternalTab] = useState<Tab>(isAdminOrOwner ? 'persona' : 'datenschutz')
  const tab: Tab = sections?.[0] ?? activeTab ?? internalTab
  const show = (t: Tab) => (sections ? sections.includes(t) : tab === t)
  const setTab = (t: Tab) => {
    if (activeTab === undefined) setInternalTab(t)
    // Wenn controlled: Parent (CortexPage) verwaltet via URL-Param,
    // keine direkte Aenderung von hier aus.
  }
  const [settings, setSettings] = useState<AriaSettings | null>(null)
  const [mailboxes, setMailboxes] = useState<SharedMailbox[]>([])
  const [groupFolders, setGroupFolders] = useState<GroupFolderGroup[]>([])
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([])
  const [auditFilter, setAuditFilter] = useState<string>('')
  // TQ #6128: Server-seitige Audit-Filter + Cursor-Paging.
  const [auditSince, setAuditSince] = useState<string>('')   // YYYY-MM-DD
  const [auditUntil, setAuditUntil] = useState<string>('')   // YYYY-MM-DD
  const [auditActor, setAuditActor] = useState<string>('')
  const [auditCursor, setAuditCursor] = useState<string | null>(null)
  const [auditHasMore, setAuditHasMore] = useState<boolean>(false)
  const [auditLoading, setAuditLoading] = useState<boolean>(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [mailboxSyncing, setMailboxSyncing] = useState(false)
  const [groupFolderSyncing, setGroupFolderSyncing] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'err' } | null>(null)
  const [mocked, setMocked] = useState(false)

  // Initial Load
  useEffect(() => {
    if (!token) return
    // TQ #8459/#10086: Nicht-Admin/Owner sehen NUR den Datenschutz-Tab (PrivacyModeSelector
    // laedt ihre Daten selbst ueber die eigene, fuer alle Mitglieder erlaubte
    // GET /api/v1/privacy-mode). Die anderen Endpoints hier sind admin/owner-only
    // Server-seitig -- ein 403 wuerde sonst per fetchJson-Fallback `mocked=true`
    // setzen und faelschlich die 'Backend nicht produktiv'-Banner zeigen.
    if (!isAdminOrOwner) return
    // Kunden-Instanz (TQ #409): diese Daten gibt es dort nicht; nichts laden, nichts erfinden.
    if (isTenantInstance()) return
    void (async () => {
      const s = await fetchJson<unknown>(
        `/api/org/${slug}/aria-settings`,
        { method: 'GET' },
        token,
        MOCK_SETTINGS(slug),
      )
      setSettings(normalizeSettings(s.data, slug))
      if (s.mocked) setMocked(true)

      // Backend liefert { tenant, count, items }, Mock-Fallback liefert
      // { items }. Beides unterstuetzen + alten mailboxes-Key als letzten
      // Fallback.
      const m = await fetchJson<{ items?: SharedMailbox[]; mailboxes?: SharedMailbox[] }>(
        `/api/org/${slug}/shared-mailboxes`,
        { method: 'GET' },
        token,
        { items: MOCK_MAILBOXES },
      )
      setMailboxes(m.data.items ?? m.data.mailboxes ?? [])

      const g = await fetchJson<{ groups: GroupFolderGroup[] }>(
        `/api/org/${slug}/groupfolder-map`,
        { method: 'GET' },
        token,
        { groups: MOCK_GROUPFOLDERS },
      )
      setGroupFolders(g.data.groups ?? [])
    })()
  }, [token, slug])

  // TQ #6128: Audit-Log mit Server-seitigen Filtern + Cursor-Paging.
  // reset=true → neue Filter-Suche (ersetzt Liste), reset=false → "Mehr laden"
  // (haengt an, nutzt auditCursor). Filter werden als Query-Params an den
  // #6127-Reader (since/until/actor/event_type-Prefix/cursor) gereicht.
  const loadAuditLog = useCallback(
    async (reset: boolean) => {
      if (!token) return
      setAuditLoading(true)
      try {
        const params = new URLSearchParams({ limit: '50' })
        if (auditFilter) params.set('event_type', `${auditFilter}*`)
        if (auditSince) params.set('since', `${auditSince}T00:00:00Z`)
        if (auditUntil) params.set('until', `${auditUntil}T23:59:59Z`)
        if (auditActor.trim()) params.set('actor', auditActor.trim())
        if (!reset && auditCursor) params.set('cursor', auditCursor)

        const a = await fetchJson<{ events: AuditEvent[]; next_cursor?: string | null }>(
          `/api/org/${slug}/audit-log?${params.toString()}`,
          { method: 'GET' },
          token,
          { events: MOCK_AUDIT },
        )
        const incoming = a.data.events ?? []
        setAuditEvents((prev) => (reset ? incoming : [...prev, ...incoming]))
        setAuditCursor(a.data.next_cursor ?? null)
        setAuditHasMore(Boolean(a.data.next_cursor))
      } finally {
        setAuditLoading(false)
      }
    },
    [token, slug, auditFilter, auditSince, auditUntil, auditActor, auditCursor],
  )

  // Initial-Load des Audit-Logs (einmal bei Mount/Token). Filter-Re-Loads
  // laufen ueber den expliziten "Anwenden"-Button in AuditTab.
  useEffect(() => {
    if (!token) return
    if (isTenantInstance()) return // Audit-Log gibt es in der Kunden-Instanz nicht (TQ #409)
    void loadAuditLog(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, slug])

  const handleSettingsChange = useCallback((patch: Partial<AriaSettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...patch } : prev))
    setDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!settings || !token) return
    setSaving(true)
    try {
      const resp = await fetchJson(
        `/api/org/${slug}/aria-settings`,
        {
          method: 'PUT',
          body: JSON.stringify({
            aria_tonality: settings.aria_tonality,
            aria_soul_override: settings.aria_soul_override,
            deferred_tools_enabled: settings.deferred_tools_enabled,
            nc_groupfolder_sync_enabled: settings.nc_groupfolder_sync_enabled,
          }),
        },
        token,
        { ok: true } as unknown,
      )
      setDirty(false)
      setToast({
        msg: resp.mocked ? 'Gespeichert (mock — Backend noch nicht live)' : 'Gespeichert',
        type: 'ok',
      })
    } catch (err) {
      setToast({ msg: `Speichern fehlgeschlagen: ${(err as Error).message}`, type: 'err' })
    } finally {
      setSaving(false)
      setTimeout(() => setToast(null), 4000)
    }
  }, [settings, token, slug])

  const handleMailboxToggle = useCallback(
    async (address: string, canRead: boolean) => {
      setMailboxes((prev) =>
        prev.map((m) => (m.address === address ? { ...m, aria_can_read: canRead } : m)),
      )
      if (!token) return
      await fetchJson(
        `/api/org/${slug}/shared-mailboxes`,
        {
          method: 'PATCH',
          body: JSON.stringify({ updates: [{ address, aria_can_read: canRead }] }),
        },
        token,
        { ok: true } as unknown,
      )
    },
    [token, slug],
  )

  const handleMailboxSync = useCallback(async () => {
    if (!token) return
    setMailboxSyncing(true)
    try {
      const resp = await fetchJson<{ job_id?: string; items?: SharedMailbox[]; mailboxes?: SharedMailbox[] }>(
        `/api/org/${slug}/shared-mailboxes/sync`,
        { method: 'POST' },
        token,
        { items: MOCK_MAILBOXES },
      )
      // Re-fetch list
      const m = await fetchJson<{ items?: SharedMailbox[]; mailboxes?: SharedMailbox[] }>(
        `/api/org/${slug}/shared-mailboxes`,
        { method: 'GET' },
        token,
        { items: resp.data.items ?? resp.data.mailboxes ?? MOCK_MAILBOXES },
      )
      setMailboxes(m.data.items ?? m.data.mailboxes ?? [])
      setToast({
        msg: resp.mocked ? 'Sync gestartet (mock)' : 'Sync gestartet',
        type: 'ok',
      })
    } finally {
      setMailboxSyncing(false)
      setTimeout(() => setToast(null), 4000)
    }
  }, [token, slug])

  const handleGroupFolderToggle = useCallback(
    async (kcGroup: string, gfId: number, visible: boolean) => {
      setGroupFolders((prev) =>
        prev.map((g) =>
          g.kc_group_path === kcGroup
            ? {
                ...g,
                groupfolders: g.groupfolders.map((gf) => (gf.id === gfId ? { ...gf, visible } : gf)),
              }
            : g,
        ),
      )
      if (!token) return
      await fetchJson(
        `/api/org/${slug}/groupfolder-map`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            updates: [{ kc_group_path: kcGroup, nc_groupfolder_id: gfId, visible }],
          }),
        },
        token,
        { ok: true } as unknown,
      )
    },
    [token, slug],
  )

  const handleGroupFolderSync = useCallback(async () => {
    if (!token) return
    setGroupFolderSyncing(true)
    try {
      await fetchJson(
        `/api/org/${slug}/groupfolder-map/sync`,
        { method: 'POST' },
        token,
        { ok: true } as unknown,
      )
      const g = await fetchJson<{ groups: GroupFolderGroup[] }>(
        `/api/org/${slug}/groupfolder-map`,
        { method: 'GET' },
        token,
        { groups: MOCK_GROUPFOLDERS },
      )
      setGroupFolders(g.data.groups ?? [])
      setSettings((prev) =>
        prev ? { ...prev, nc_groupfolder_sync_last_run: new Date().toISOString() } : prev,
      )
      setToast({ msg: 'Sync abgeschlossen', type: 'ok' })
    } finally {
      setGroupFolderSyncing(false)
      setTimeout(() => setToast(null), 4000)
    }
  }, [token, slug])

  if (!settings && isAdminOrOwner) {
    return <div className="aria-org-loading">Lade Aria-Einstellungen…</div>
  }

  // Nur Admin/Owner duerfen alle Tabs; Datenschutz ist Lesezugriff fuer alle
  // Mitglieder (TQ #8459/#10086). Aendern bleibt server-seitig admin/owner-only.
  if (!isAdminOrOwner && !show('datenschutz')) {
    return (
      <div className="aria-org-page">
        <div className="aria-org-noaccess">
          Aria-Org-Einstellungen sind nur fuer Owner und Admins zugaenglich.
        </div>
      </div>
    )
  }

  return (
    <div className={`aria-org-page ${hideTabBar ? 'aria-org-page--embedded' : ''}`}>
      {!hideTabBar && (
        <header className="aria-org-header">
          <h2>Aria-Einstellungen für {organization.name}</h2>
          {mocked && (
            <div className="aria-org-mock-banner">
              ⚠️ Backend portal-api Endpoints noch nicht produktiv — UI zeigt Mock-Daten.
              Änderungen werden NICHT persistiert. (Bundle-C #6165 ausstehend.)
            </div>
          )}
        </header>
      )}

      {hideTabBar && mocked && (
        <div className="aria-org-mock-banner">
          ⚠️ Backend portal-api Endpoints noch nicht produktiv — UI zeigt Mock-Daten.
          Änderungen werden NICHT persistiert. (Bundle-C #6165 ausstehend.)
        </div>
      )}

      {!hideTabBar && (
        <nav className="aria-org-tabs">
          {(
            [
              { id: 'persona', label: 'Persona' },
              { id: 'defaults', label: 'Org-Defaults' },
              { id: 'datenschutz', label: 'Datenschutz' },
              { id: 'groupfolders', label: 'GroupFolders' },
              { id: 'audit', label: 'Audit-Log' },
            ] as { id: Tab; label: string }[]
          )
            .filter((t) => isAdminOrOwner || t.id === 'datenschutz')
            .map((t) => (
              <button
                key={t.id}
                className={`aria-org-tab-btn ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
        </nav>
      )}

      <main className="aria-org-content">
        {show('persona') && settings && (
          <PersonaTab settings={settings} onChange={handleSettingsChange} />
        )}
        {show('datenschutz') && <PrivacyModeSelector canEdit={isAdminOrOwner} />}
        {show('defaults') && settings && (
          <DefaultsTab
            settings={settings}
            mailboxes={mailboxes}
            onSettingsChange={handleSettingsChange}
            onMailboxToggle={handleMailboxToggle}
            onMailboxSync={handleMailboxSync}
            mailboxSyncing={mailboxSyncing}
          />
        )}
        {show('groupfolders') && settings && (
          <GroupFoldersTab
            groups={groupFolders}
            syncEnabled={settings.nc_groupfolder_sync_enabled}
            lastSyncRun={settings.nc_groupfolder_sync_last_run}
            onToggleVisible={handleGroupFolderToggle}
            onSync={handleGroupFolderSync}
            syncing={groupFolderSyncing}
            onSyncEnabledChange={(e) =>
              handleSettingsChange({ nc_groupfolder_sync_enabled: e })
            }
          />
        )}
        {show('audit') && (
          <AuditTab
            events={auditEvents}
            filterType={auditFilter}
            setFilterType={setAuditFilter}
            since={auditSince}
            setSince={setAuditSince}
            until={auditUntil}
            setUntil={setAuditUntil}
            actor={auditActor}
            setActor={setAuditActor}
            onApply={() => void loadAuditLog(true)}
            onLoadMore={() => void loadAuditLog(false)}
            hasMore={auditHasMore}
            loading={auditLoading}
          />
        )}
      </main>

      {(show('persona') || show('defaults') || show('groupfolders')) && (
        <footer className="aria-org-footer">
          <button
            className="aria-org-btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
          {!dirty && <span className="aria-org-no-changes">Keine ungespeicherten Änderungen</span>}
        </footer>
      )}

      {toast && <div className={`aria-org-toast aria-org-toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  )
}

// Kunden-Instanz (TQ #409): aria-settings, shared-mailboxes, groupfolder-map und audit-log
// bedient cockpit-api nicht -> "nicht verfuegbar" statt 404/Mock. Datenschutz bleibt.
const TENANT_UNAVAILABLE: Record<string, string> = {
  persona: 'Aria-Einstellungen (Persona)',
  defaults: 'Aria-Einstellungen (Org-Defaults)',
  groupfolders: 'Gruppenordner und gemeinsame Postfächer',
  audit: 'Audit-Log',
}

export default function AriaOrgSettings(props: AriaOrgSettingsProps) {
  if (!isTenantInstance()) return <AriaOrgSettingsInner {...props} />
  const wanted: readonly Tab[] | undefined = props.sections ?? (props.activeTab ? [props.activeTab] : undefined)
  const blocked = (wanted ?? (Object.keys(TENANT_UNAVAILABLE) as Tab[])).filter((t) => t in TENANT_UNAVAILABLE)
  const allowed = wanted?.filter((t) => !(t in TENANT_UNAVAILABLE)) ?? []
  return (
    <>
      {blocked.map((t) => <NotAvailableNotice key={t} feature={TENANT_UNAVAILABLE[t]} />)}
      {allowed.length > 0 && <AriaOrgSettingsInner {...props} sections={allowed} />}
    </>
  )
}
